// Colours (mechanics §5.1, §12.5, §17.3; art-assets §2). A colour is a 24-bit integer 0xRRGGBB,
// the same value the protocol's `rgb` type carries.

import { hash32 } from './math/hash.js';
import type { Rng } from './math/rng.js';

/** A 24-bit colour, 0xRRGGBB. */
export type Rgb = number;

/** Parses `#RRGGBB` or `#RGB` (case-insensitive, `#` optional). Throws on anything else. */
export function hexToRgb(hex: string): Rgb {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  if (/^[0-9a-fA-F]{6}$/.test(h)) return parseInt(h, 16);
  if (/^[0-9a-fA-F]{3}$/.test(h)) {
    const [r = '0', g = '0', b = '0'] = h;
    return parseInt(r + r + g + g + b + b, 16);
  }
  throw new RangeError(`not a hex colour: ${JSON.stringify(hex)}`);
}

/** Formats as `#rrggbb` (lower case). */
export function rgbToHex(c: Rgb): string {
  return '#' + (c & 0xffffff).toString(16).padStart(6, '0');
}

export function rgbChannels(c: Rgb): [r: number, g: number, b: number] {
  return [(c >>> 16) & 0xff, (c >>> 8) & 0xff, c & 0xff];
}

/** Builds a colour from channels; each is rounded and clamped to 0–255. */
export function rgbFromChannels(r: number, g: number, b: number): Rgb {
  const ch = (v: number): number => (v <= 0 ? 0 : v >= 255 ? 255 : Math.round(v));
  return (ch(r) << 16) | (ch(g) << 8) | ch(b);
}

/** `rgb(r g b)` or `rgb(r g b / a)` for canvas and CSS. */
export function rgbToCss(c: Rgb, alpha = 1): string {
  const [r, g, b] = rgbChannels(c);
  return alpha >= 1 ? `rgb(${r} ${g} ${b})` : `rgb(${r} ${g} ${b} / ${alpha})`;
}

/** Multiplies every channel by `k` (k < 1 darkens toward black, k > 1 brightens, clamped). */
export function scaleRgb(c: Rgb, k: number): Rgb {
  const [r, g, b] = rgbChannels(c);
  return rgbFromChannels(r * k, g * k, b * k);
}

/** Mixes `a` toward `b`: `t = 0` is `a`, `t = 1` is `b`. */
export function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  const [ar, ag, ab] = rgbChannels(a);
  const [br, bg, bb] = rgbChannels(b);
  return rgbFromChannels(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** WCAG relative luminance in [0, 1]. */
export function relativeLuminance(c: Rgb): number {
  const lin = (v: number): number => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = rgbChannels(c);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two colours, from 1 to 21 (art-assets §2 palette rule). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Minimum contrast of every palette colour against both background colours (art-assets §2). */
export const PALETTE_MIN_CONTRAST = 1.6;

// ---------------------------------------------------------------------------------------------
// Default colours (art-assets §2 theme.json defaults).
// ---------------------------------------------------------------------------------------------

/** The 24-colour player palette, as hex strings (art-assets §2, mechanics §12.5). */
export const PLAYER_PALETTE_HEX: readonly string[] = Object.freeze([
  '#E8453C', '#F28C28', '#F5C518', '#9BCB3B', '#3DBE5A', '#1FB5A7', '#2EA8E6', '#3D6FE0',
  '#6A4FD8', '#A24FD0', '#E04FA8', '#F06B8B', '#C8553D', '#E8A33C', '#C9D23C', '#4FB286',
  '#3BA3C2', '#5B8DEF', '#8C6BE8', '#D06BE0', '#FF7F6E', '#7FD46B', '#FFB000', '#19A0FF',
]); // prettier-ignore

/**
 * The 24-colour palette as 0xRRGGBB values: UI art, brand letters and theme.json-style palettes.
 * New lives and pellets use the generated agar-like colours instead (visuals §2).
 */
export const PLAYER_PALETTE: readonly Rgb[] = Object.freeze(PLAYER_PALETTE_HEX.map(hexToRgb));

/** Team colours for Teams: red, green, blue (mechanics §17.3). */
export const TEAM_COLORS: readonly Rgb[] = Object.freeze([0xe5484d, 0x30a46c, 0x3e63dd]);

/** Team colours for the optional Team Rush variant: red and blue (mechanics §17.6). */
export const TEAM_RUSH_COLORS: readonly Rgb[] = Object.freeze([0xe5484d, 0x3e63dd]);

/** Special colours (art-assets §2). */
export const SPECIAL_COLORS = Object.freeze({
  /** agar-like virus green (visuals §7); its 10 u band is `classicOutlineColor` = #2de52d. */
  virus: 0x33ff33,
  spawner: 0xd1495b,
  /** "Plain colors" setting: other players' cells and pellets in neutral grey (ui-flow §4.5). */
  plain: 0x9aa0a6,
  ink: 0x2b2b2b,
  white: 0xffffff,
});

// ---------------------------------------------------------------------------------------------
// Render styles and their background palettes.
//
// Owner override: the default style is "classic" (light near-white background with a thin grey
// grid, solid cells with a darker same-hue outline). "sketch" is the spec's paper-and-ink look
// (art-assets §2, §5). Dark mode is a dark background variant of whichever style is active.
// ---------------------------------------------------------------------------------------------

export const RENDER_STYLES = ['classic', 'sketch'] as const;
export type RenderStyle = (typeof RENDER_STYLES)[number];
export const DEFAULT_RENDER_STYLE: RenderStyle = 'classic';

export type ThemeMode = 'light' | 'dark';

export interface ArenaColors {
  /** Arena fill inside the map. */
  background: Rgb;
  /** Minor (50 u) grid lines. */
  grid: Rgb;
  /** Major (250 u) grid lines. */
  gridMajor: Rgb;
  /** Map border line. */
  border: Rgb;
  /** Shade over the area outside the map. */
  outside: Rgb;
  /** Cell name text fill. */
  nameFill: Rgb;
  /** Cell name text outline. */
  nameOutline: Rgb;
}

export const ARENA_COLORS: Readonly<Record<RenderStyle, Readonly<Record<ThemeMode, ArenaColors>>>> =
  Object.freeze({
    // agar-like (visuals §1): no outside shade and no border line, so `outside` and `border`
    // equal the background; `grid` is the opaque look of the translucent grid (CLASSIC_GRID)
    // for consumers that cannot draw alpha.
    classic: {
      light: {
        background: 0xf2fbff,
        grid: 0xc2c9cc,
        gridMajor: 0xc2c9cc,
        border: 0xf2fbff,
        outside: 0xf2fbff,
        nameFill: 0xffffff,
        nameOutline: 0x000000,
      },
      dark: {
        background: 0x111111,
        grid: 0x2c2c2c,
        gridMajor: 0x2c2c2c,
        border: 0x111111,
        outside: 0x111111,
        nameFill: 0xffffff,
        nameOutline: 0x000000,
      },
    },
    sketch: {
      light: {
        background: 0xfbf7ee,
        grid: 0x7fa6c9,
        gridMajor: 0x7fa6c9,
        border: 0x2b2b2b,
        outside: 0xece6da,
        nameFill: 0xffffff,
        nameOutline: 0x2b2b2b,
      },
      dark: {
        background: 0x1e2622,
        grid: 0xe8e8e0,
        gridMajor: 0xe8e8e0,
        border: 0xe8e8e0,
        outside: 0x161c19,
        nameFill: 0xffffff,
        nameOutline: 0x111111,
      },
    },
  });

/** The classic grid (visuals §1): one 50 u grid of 1 world-unit lines in a translucent colour. */
export const CLASSIC_GRID = Object.freeze({
  spacingU: 50,
  light: Object.freeze({ color: 0x000000 as Rgb, alpha: 0.2 }),
  dark: Object.freeze({ color: 0xffffff as Rgb, alpha: 0.11 }),
});

/** Outline darkening for the classic style: the cell colour with every channel × this factor. */
export const CLASSIC_OUTLINE_FACTOR = 0.9;

/**
 * The classic look's 10 u cell band (visuals §2): every channel × 0.9, floored
 * (`#0723FF → #061FE5`, virus `#33FF33 → #2DE52D`).
 */
export function classicOutlineColor(c: Rgb): Rgb {
  const k = CLASSIC_OUTLINE_FACTOR;
  const r = Math.floor(((c >>> 16) & 0xff) * k);
  const g = Math.floor(((c >>> 8) & 0xff) * k);
  const b = Math.floor((c & 0xff) * k);
  return (r << 16) | (g << 8) | b;
}

/** Money-mode accents (money-mode §7): the cash-out ring and the value labels. */
export const MONEY_COLORS = Object.freeze({
  /** Cash-out ring and value label fill. */
  gold: 0xffc21a as Rgb,
  /** The ring's dark backing (contrast on the light arena). */
  goldShadow: 0x5a3f00 as Rgb,
  /** Practice rooms (money-mode §1.4): play-money labels and ring in silver instead of gold. */
  silver: 0xdfe4ea as Rgb,
  silverShadow: 0x3c434c as Rgb,
});

// ---------------------------------------------------------------------------------------------
// Generated agar-like colours (visuals §2): a random channel order, one channel 0xFF, one 0x07,
// the third any value 0–255. 6 orders × 256 values = 1536 colours.
// ---------------------------------------------------------------------------------------------

/** The "full" and "low" channel values of a generated colour. */
export const GENERATED_HIGH = 0xff;
export const GENERATED_LOW = 0x07;

/** Number of channel orders (permutations of high / low / free over r, g, b). */
export const GENERATED_ORDERS = 6;

/**
 * The generated colour for channel order `order` (0–5) and free channel value `free` (0–255).
 * Order k assigns (high, low, free) to (r, g, b) as: 0 → (r, g, b), 1 → (r, b, g),
 * 2 → (g, r, b), 3 → (g, b, r), 4 → (b, r, g), 5 → (b, g, r).
 */
export function generatedColor(order: number, free: number): Rgb {
  const f = free <= 0 ? 0 : free >= 255 ? 255 : Math.floor(free);
  const H = GENERATED_HIGH;
  const L = GENERATED_LOW;
  switch (((Math.floor(order) % 6) + 6) % 6) {
    case 0:
      return (H << 16) | (L << 8) | f;
    case 1:
      return (H << 16) | (f << 8) | L;
    case 2:
      return (L << 16) | (H << 8) | f;
    case 3:
      return (f << 16) | (H << 8) | L;
    case 4:
      return (L << 16) | (f << 8) | H;
    default:
      return (f << 16) | (L << 8) | H;
  }
}

/** The generated colour picked by a 32-bit value: order = `u mod 6`, free = `(u >>> 8) & 0xff`. */
export function generatedColorFromU32(u: number): Rgb {
  const v = u >>> 0;
  return generatedColor(v % GENERATED_ORDERS, (v >>> 8) & 0xff);
}

/** True when a colour has the generated shape: one channel 0xFF, one 0x07 (visuals §2). */
export function isGeneratedColor(c: Rgb): boolean {
  const ch = rgbChannels(c);
  const hi = ch.indexOf(GENERATED_HIGH);
  if (hi < 0) return false;
  for (let i = 0; i < 3; i++) if (i !== hi && ch[i] === GENERATED_LOW) return true;
  return false;
}

// ---------------------------------------------------------------------------------------------
// Colour rules.
// ---------------------------------------------------------------------------------------------

/** Pellet colour index: `hash32(pelletId) mod paletteLength` (mechanics §5.1). */
export function pelletColorIndex(pelletId: number, paletteLength: number): number {
  return paletteLength > 0 ? hash32(pelletId) % paletteLength : 0;
}

/**
 * The pellet's colour: by default the generated agar-like colour from `hash32(id)` (visuals §2:
 * order = h mod 6, free channel = (h >>> 8) & 0xFF); with an explicit `palette` (theme.json),
 * `palette[hash32(id) mod length]` (mechanics §5.1).
 */
export function pelletColor(pelletId: number, palette?: readonly Rgb[]): Rgb {
  if (palette) return palette[pelletColorIndex(pelletId, palette.length)] ?? SPECIAL_COLORS.plain;
  return generatedColorFromU32(hash32(pelletId));
}

/**
 * A colour for a new life: by default a generated agar-like colour (visuals §2); with a
 * `palette`, a uniform pick from it (mechanics §12.5). Either way exactly one RNG draw, so the
 * room's RNG sequence (and the determinism goldens) do not depend on the colour rule.
 */
export function randomPlayerColor(rng: Rng, palette?: readonly Rgb[]): Rgb {
  if (palette) return rng.pick(palette);
  return generatedColorFromU32(rng.nextU32());
}

/**
 * Team colour with a random offset of up to ±`jitter` in each channel, clamped to 0–255
 * (mechanics §17.3: ±24 by default).
 */
export function jitterColor(c: Rgb, jitter: number, rng: Rng): Rgb {
  const [r, g, b] = rgbChannels(c);
  const j = Math.max(0, Math.floor(jitter));
  return rgbFromChannels(r + rng.intRange(-j, j), g + rng.intRange(-j, j), b + rng.intRange(-j, j));
}
